import React from 'react';
import { FileText, CheckCircle, Shield, Award, ArrowRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { ProcessSlider } from '../components/ProcessSlider';

export const CopyrightRegistration = ({ onOpenPopup }) => {
  return (
    <div className="min-h-screen">
      {/* Hero Banner */}
      <section className="pt-32 pb-20 relative bg-gradient-to-br from-red-600/95 via-red-700/95 to-red-800/95" style={{backgroundImage: 'url(https://images.pexels.com/photos/8793796/pexels-photo-8793796.jpeg)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundBlendMode: 'overlay'}}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 bg-yellow-400 px-4 py-2 rounded-full mb-6">
              <FileText className="w-5 h-5 text-gray-900" />
              <span className="text-gray-900 font-semibold">Creative Work Protection</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Professional Copyright <span className="text-yellow-400">Registration</span>
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Protect your creative works with official copyright registration. Secure legal protection for books, music, software, art, and more.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={onOpenPopup}
                className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300"
              >
                Register Your Copyright
              </button>
              <button
                onClick={onOpenPopup}
                className="bg-yellow-400 hover:bg-yellow-500 text-gray-900 px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300"
              >
                Get Expert Advice
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Info Section with Image */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">
                Protect Your <span className="text-red-600">Creative Works</span>
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Copyright registration provides legal proof of ownership for your original creative works. Whether you've written a book, composed music, developed software, or created artwork, copyright protection ensures that you maintain exclusive rights to reproduce, distribute, and profit from your creation.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                While copyright exists automatically upon creation, official registration with the U.S. Copyright Office provides significant legal advantages. You can't sue for infringement without registration, and early registration allows you to claim statutory damages and attorney's fees.
              </p>
              <div className="space-y-3">
                {[
                  'Sue infringers in federal court',
                  'Claim statutory damages up to $150,000',
                  'Recover attorney\'s fees in lawsuits',
                  'Public record of ownership',
                  'Protection against plagiarism',
                  'Licensing and monetization rights'
                ].map((item, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src="https://images.unsplash.com/photo-1455390582262-044cdead277a?w=800&h=600&fit=crop"
                alt="Creative works protection"
                className="rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What Can Be Copyrighted */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Can Be <span className="text-red-600">Copyrighted?</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Copyright protection applies to a wide range of original creative works.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                title: 'Literary Works',
                items: ['Books & manuscripts', 'Articles & blog posts', 'Poetry & essays', 'Computer programs']
              },
              {
                title: 'Musical Works',
                items: ['Songs & compositions', 'Lyrics', 'Musical recordings', 'Sound effects']
              },
              {
                title: 'Artistic Works',
                items: ['Paintings & drawings', 'Photographs', 'Sculptures', 'Graphic designs']
              },
              {
                title: 'Audiovisual Works',
                items: ['Movies & films', 'Video games', 'Television shows', 'Online videos']
              },
              {
                title: 'Architectural Works',
                items: ['Building designs', 'Blueprints', 'Technical drawings', 'Floor plans']
              },
              {
                title: 'Dramatic Works',
                items: ['Plays & scripts', 'Choreography', 'Pantomimes', 'Stage productions']
              }
            ].map((category, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{category.title}</h3>
                <ul className="space-y-2">
                  {category.items.map((item, idx) => (
                    <li key={idx} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-red-600 rounded-full"></div>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Registration <span className="text-red-600">Benefits</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: Shield,
                title: 'Legal Protection',
                description: 'Required to file infringement lawsuits in federal court'
              },
              {
                icon: Award,
                title: 'Statutory Damages',
                description: 'Claim up to $150,000 per work without proving actual damages'
              },
              {
                icon: FileText,
                title: 'Public Record',
                description: 'Establishes official ownership record with the U.S. government'
              },
              {
                icon: CheckCircle,
                title: 'Attorney Fees',
                description: 'Recover legal costs if you win an infringement case'
              }
            ].map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="bg-red-100 w-16 h-16 rounded-full flex items-center justify-center mb-4 mx-auto">
                  <benefit.icon className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Copyright Registration <span className="text-red-600">Process</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Simple, straightforward copyright registration with expert guidance at every step.
            </p>
          </div>
          <ProcessSlider />
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - FAQ Content */}
            <div className="space-y-6">
              <div className="mb-8">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">
                  Frequently Asked <span className="text-red-600">Questions</span>
                </h2>
                <p className="text-lg text-gray-600">
                  Common questions about copyright registration answered by our experts.
                </p>
              </div>

              <Accordion type="single" collapsible className="space-y-4">
                {[
                  {
                    q: 'What can I copyright?',
                    a: 'You can copyright original works of authorship including literary works, music, dramatic works, choreography, pictorial/graphic works, motion pictures, sound recordings, and architectural works.'
                  },
                  {
                    q: 'How long does copyright protection last?',
                    a: 'For works created after 1978, copyright lasts for the life of the author plus 70 years. For works made for hire, the duration is 95 years from publication or 120 years from creation, whichever is shorter.'
                  },
                  {
                    q: 'Do I need to register to have copyright protection?',
                    a: 'Copyright exists automatically upon creation, but registration provides significant legal advantages including the ability to sue for infringement and claim statutory damages and attorney fees.'
                  },
                  {
                    q: 'How much does copyright registration cost?',
                    a: 'Our service includes comprehensive copyright registration assistance. U.S. Copyright Office filing fees are additional and vary based on the type of work being registered.'
                  },
                  {
                    q: 'How long does copyright registration take?',
                    a: 'The U.S. Copyright Office typically processes applications in 3-6 months for standard processing. We file your application promptly and keep you updated throughout the process.'
                  }
                ].map((faq, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="bg-white rounded-lg shadow-md border-none">
                    <AccordionTrigger className="px-6 py-4 text-left hover:no-underline">
                      <span className="text-lg font-bold text-gray-900">{faq.q}</span>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-4 text-gray-600 leading-relaxed">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            {/* Right Side - Image */}
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1455390582262-044cdead277a?w=800&h=1000&fit=crop"
                alt="Copyright protection FAQs"
                className="rounded-2xl shadow-2xl w-full h-auto object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-yellow-400 text-gray-900 p-6 rounded-xl shadow-xl max-w-xs">
                <div className="text-3xl font-bold mb-1">Legal</div>
                <div className="text-sm font-semibold">Protection for Your Creative Works</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-red-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Protect Your Creative Work Today
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Don't leave your valuable creative works unprotected. Register your copyright and secure your legal rights.
          </p>
          <button
            onClick={onOpenPopup}
            className="bg-white text-red-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all duration-300"
          >
            Start Copyright Registration
          </button>
        </div>
      </section>
    </div>
  );
};